function aceJumpBottom(viewport, menu, obstacles, base, gap = 10) {
  let bottom = base;
  for (let pass = 0; pass <= obstacles.length; pass++) {
    let changed = false;
    for (const box of obstacles) {
      if (!box || box.width <= 0 || box.height <= 0) continue;
      const top = viewport.height - bottom - menu.height;
      const edge = viewport.height - bottom;
      if (box.left < menu.right && box.right > menu.left && box.top - gap < edge && box.bottom + gap > top) {
        const next = viewport.height - box.top + gap;
        if (next > bottom) { bottom = next; changed = true; }
      }
    }
    if (!changed) break;
  }
  return Math.max(base, Math.round(bottom));
}
if (typeof module !== 'undefined' && module.exports) module.exports = { aceJumpBottom };
if (typeof customElements !== 'undefined' && !customElements.get('ace-home-jump')) {
  customElements.define('ace-home-jump', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.options = { signal: this.controller.signal };
      this.toggle = this.querySelector('[data-jump-toggle]');
      this.panel = this.querySelector('[id="AceHomeJumpPanel"]');
      this.links = Array.from(this.querySelectorAll('[data-section-suffix]'));
      this.sections = [];
      this.links.forEach((link) => {
        const section = document.querySelector(`#MainContent > [id$="__${link.dataset.sectionSuffix}"]`);
        link.parentElement.hidden = !section;
        if (section) {
          link.href = `#${section.id}`;
          this.sections.push({ link, section });
        }
      });
      this.hidden = this.sections.length === 0;
      this.setOpen(false);
      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-jump-toggle]');
        const top = event.target.closest('[data-jump-top]');
        const link = event.target.closest('[data-section-suffix]');
        if (toggle) {
          this.setOpen(this.panel.hidden);
          if (!this.panel.hidden) this.sections[0]?.link.focus();
        } else if ((top || link) && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          this.go(top ? 'top' : link.hash.slice(1), true, true);
        }
      }, this.options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.panel.hidden) {
          event.preventDefault();
          this.setOpen(false);
          this.toggle.focus();
        }
      }, this.options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      }, this.options);
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.setOpen(false);
      }, this.options);
      this.scheduleUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.updateActive();
        });
      };
      window.addEventListener('scroll', this.scheduleUpdate, { ...this.options, passive: true });
      document.addEventListener('ace:announcement-resize', this.scheduleUpdate, this.options);
      this.restoreHash = (event) => {
        const id = window.location.hash.slice(1);
        if (id === 'top' || this.sections.some(({ section }) => section.id === id)) this.go(id, false, Boolean(event && (event.type === 'popstate' || event.type === 'hashchange')));
      };
      window.addEventListener('popstate', this.restoreHash, this.options);
      window.addEventListener('hashchange', this.restoreHash, this.options);
      window.addEventListener('pageshow', () => { this.setOpen(false); this.measure(); this.scheduleUpdate(); }, this.options);
      window.addEventListener('resize', () => { this.measure(); this.scheduleUpdate(); }, this.options);
      window.addEventListener('load', () => { this.measure(); this.restoreHash(); }, this.options);
      this.observedControls = new WeakSet();
      this.schedulePlacement = () => {
        if (this.placementFrame) return;
        this.placementFrame = requestAnimationFrame(() => { this.placementFrame = 0; this.measure(); });
      };
      this.controlObserver = new MutationObserver(this.schedulePlacement);
      document.addEventListener('ace:board-ready', this.schedulePlacement, this.options);
      window.visualViewport?.addEventListener('resize', this.schedulePlacement, this.options);
      this.previewObserver = new MutationObserver((records) => {
        if (records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1 && (node.tagName === 'IFRAME' || node.tagName === 'SHOPIFY-CHAT' || node.matches?.('[data-ace-board-root]') || node.id === 'PBarNextFrameWrapper')))) this.measure();
      });
      this.previewObserver.observe(document.body, { childList: true, subtree: true });
      this.measure();
      this.updateActive();
      this.restoreHash();
    }
    setOpen(open) {
      this.panel.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
    }
    measure() {
      const preview = document.getElementById('PBarNextFrameWrapper') || document.getElementById('PBarNextFrame');
      const height = document.documentElement.clientHeight;
      const chatRoot = document.querySelector('shopify-chat')?.shadowRoot;
      const boardRoot = document.querySelector('[data-ace-board-root]');
      for (const control of [chatRoot, boardRoot]) {
        if (control && this.controlObserver && !this.observedControls.has(control)) {
          this.observedControls.add(control);
          this.controlObserver.observe(control, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-expanded'] });
        }
      }
      const controls = [chatRoot?.querySelector('button[part="activator"]'), boardRoot?.querySelector('.ace-board-launcher[data-ace-board-open]')];
      const obstacles = controls.filter(control => control && !control.hidden && getComputedStyle(control).visibility !== 'hidden').map(control => control.getBoundingClientRect());
      const actions = this.querySelector('.ace-home-jump__actions')?.getBoundingClientRect();
      const base = preview ? 88 : 20;
      const bottom = actions && height ? aceJumpBottom({ height }, actions, obstacles, base) : base;
      const value = `${bottom}px`;
      if (this.style.getPropertyValue('--ace-jump-bottom') !== value) this.style.setProperty('--ace-jump-bottom', value);
    }
    offset() {
      const header = document.querySelector('.section-header');
      const sticky = header?.classList.contains('shopify-section-header-sticky') && !header.classList.contains('shopify-section-header-hidden');
      const bar = document.querySelector('.ace-category-navigation-section');
      const bandHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ace-sticky-announcement-height')) || 0;
      const announcement = document.querySelector('.ace-member-announcement-section');
      const bandBottom = announcement ? Math.max(0, announcement.getBoundingClientRect().bottom) : bandHeight;
      const headerBottom = sticky ? Math.max(header.getBoundingClientRect().bottom, bandHeight + header.offsetHeight) : 0;
      return Math.max(bandBottom, headerBottom) + (bar?.offsetHeight || 0) + 12;
    }
    go(id, record, focus) {
      const target = id === 'top' ? null : this.sections.find(({ section }) => section.id === id)?.section;
      if (id !== 'top' && !target) return;
      this.setOpen(false);
      if (record && window.location.hash !== `#${id}`) window.history.pushState(window.history.state, '', `#${id}`);
      const version = this.jumpVersion = (this.jumpVersion || 0) + 1;
      const align = () => {
        if (version !== this.jumpVersion) return;
        const top = target ? target.getBoundingClientRect().top + window.scrollY - this.offset() : 0;
        window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
        this.updateActive();
      };
      align();
      requestAnimationFrame(() => requestAnimationFrame(align));
      if (focus) {
        const heading = target?.querySelector('h1,h2');
        const headingRect = heading?.getBoundingClientRect();
        const focusTarget = target ? (headingRect && headingRect.height > 2 ? heading : target) : document.querySelector('.header__heading-link');
        if (focusTarget) {
          const temporary = !focusTarget.hasAttribute('tabindex') && !focusTarget.matches('a,button,input,select,textarea');
          if (temporary) focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
          if (temporary) focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
        }
      }
    }
    updateActive() {
      let current = null;
      const edge = this.offset() + 30;
      this.sections.forEach(({ section }) => { if (section.getBoundingClientRect().top <= edge) current = section; });
      this.sections.forEach(({ link, section }) => {
        if (section === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.previewObserver?.disconnect();
      this.controlObserver?.disconnect();
      cancelAnimationFrame(this.placementFrame);
      cancelAnimationFrame(this.frame);
      this.jumpVersion = (this.jumpVersion || 0) + 1;
    }
  });
}


